import { createContext, useState, useEffect } from "react";
import authService from "../services/authService";
import userService from "../services/userService";
import { jwtDecode } from "jwt-decode";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const getUserIdFromToken = () => {
        const token = localStorage.getItem("authToken");
        if (!token) return null;
        try {
            const decoded = jwtDecode(token);
            return decoded.nameid; // This is ClaimTypes.NameIdentifier from backend
        } catch (error) {
            console.error("Error decoding token:", error);
            return null;
        }
    };

    //Load user authentication state on app start
    useEffect(() => {
        const loadUser = async () => {
            const userId = getUserIdFromToken();
            if (userId) {
                try {
                    const userData = await userService.getUserDetails(userId);
                    if (userData) {
                        // Ensure userData has an id property
                        if (!userData.id) {
                            userData.id = userId;
                        }
                        setUser(userData);
                        setIsAuthenticated(true);
                    }
                } catch (error) {
                    console.error("Failed to load user:", error);
                    authService.logout(); // Clear invalid session
                }
            }
            setLoading(false);
        };
        loadUser();
    }, []);

    //Login function
    const login = async (credentials) => {
        const response = await authService.login(credentials);
        if (!response.requiresTwoFactor) {
            const userId = response.userId;
            if (userId) {
                const userData = await userService.getUserDetails(userId);
                if (userData) {
                    // Ensure userData has an id property
                    if (!userData.id) {
                        userData.id = userId;
                    }
                    setUser(userData);
                    setIsAuthenticated(true);
                }
            }
        }
        return response;
    };

    const verifyTwoFactor = async (twoFactorCode) => {
        const response = await authService.verifyTwoFactor(twoFactorCode);
        if (response.userId) {
            const userData = await userService.getUserDetails(response.userId);
            if (userData) {
                // Ensure userData has an id property
                if (!userData.id) {
                    userData.id = response.userId;
                }
                setUser(userData);
                setIsAuthenticated(true);
            }
        }
        return response;
    };

    //Logout function
    const logout = () => {
        authService.logout();
        setUser(null);
        setIsAuthenticated(false);
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login, logout, verifyTwoFactor, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
